import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, Lock, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');

  useEffect(() => {
    const handleRecoveryToken = async () => {
      const hash = window.location?.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const accessToken = params?.get('access_token');
      const refreshToken = params?.get('refresh_token');
      const type = params?.get('type');

      if (accessToken && type === 'recovery') {
        try {
          const { data, error: sessionErr } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionErr) {
            setSessionError('Your password reset link has expired or is invalid. Please request a new one.');
            return;
          }

          if (data?.session) {
            window.history?.replaceState(null, '', window.location?.pathname);
            setSessionReady(true);
          }
        } catch {
          setSessionError('Failed to process reset link. Please try again or request a new one.');
        }
      } else {
        // No token in URL — check if there's already an active recovery session
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session) {
          setSessionReady(true);
        } else {
          setSessionError('No valid reset token found. Please use the link from your password reset email.');
        }
      }
    };

    handleRecoveryToken();
  }, []);

  // Also listen for PASSWORD_RECOVERY auth event
  useEffect(() => {
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        window.history?.replaceState(null, '', window.location?.pathname);
        setSessionReady(true);
      }
    });
    return () => subscription?.unsubscribe();
  }, []);

  const passwordStrength = () => {
    if (!password) return null;
    if (password?.length < 6) return { label: 'Too short', color: '#ef4444', width: '25%' };
    if (password?.length < 8) return { label: 'Weak', color: '#f97316', width: '40%' };
    if (password?.length < 12 && !/[^a-zA-Z0-9]/?.test(password)) return { label: 'Fair', color: '#eab308', width: '60%' };
    if (password?.length >= 8 && /[A-Z]/?.test(password) && /[0-9]/?.test(password)) return { label: 'Strong', color: '#22c55e', width: '100%' };
    return { label: 'Good', color: '#ffd700', width: '80%' };
  };

  const strength = passwordStrength();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (password?.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });

      if (updateError) {
        setError(updateError?.message || 'Failed to update password. Please try again.');
        return;
      }

      setSuccess(true);

      // Sign out so user must log in fresh with new password
      await supabase?.auth?.signOut();

      setTimeout(() => {
        navigate('/admin-login', { replace: true, state: { message: 'Password updated successfully. Please log in.' } });
      }, 2500);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #060e1e 0%, #0a1628 50%, #0d1f3c 100%)' }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,215,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>
      <div className="relative w-full max-w-md mx-auto px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)' }}>
            <Lock className="w-8 h-8" style={{ color: '#0a1628' }} />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            On<span style={{ color: '#ffd700' }}>TheGo</span>
          </h1>
          <p className="text-white/40 text-sm mt-1 tracking-widest uppercase">Password Reset</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 p-8" style={{ background: 'rgba(13, 31, 60, 0.8)', backdropFilter: 'blur(20px)' }}>

          {/* Session error */}
          {sessionError ? (
            (<div className="text-center py-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Invalid Reset Link</h2>
              <p className="text-white/50 text-sm leading-relaxed">{sessionError}</p>
              <button
                onClick={() => navigate('/admin-login', { replace: true })}
                className="mt-6 text-sm transition-colors"
                style={{ color: '#ffd700' }}
              >
                ← Back to Login
              </button>
            </div>)

          /* Success state */
          ) : success ? (
            (<div className="text-center py-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(34,197,94,0.15)' }}>
                <CheckCircle2 className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Password Updated!</h2>
              <p className="text-white/50 text-sm">Password updated successfully. Please log in.</p>
              <div className="mt-4 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#ffd700' }} />
              </div>
            </div>)

          /* Loading session */
          ) : !sessionReady ? (
            (<div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: '#ffd700' }} />
              <p className="text-white/50 text-sm">Verifying your reset link…</p>
            </div>)

          /* Reset form */
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white">Reset Your Password</h2>
                <p className="text-white/40 text-sm mt-1">Enter a new password for your account</p>
              </div>

              {error && (
                <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* New Password */}
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e?.target?.value); setError(''); }}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {strength && (
                    <div className="mt-2">
                      <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-300" style={{ width: strength?.width, background: strength?.color }} />
                      </div>
                      <p className="text-xs mt-1" style={{ color: strength?.color }}>{strength?.label}</p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e?.target?.value); setError(''); }}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs mt-1 text-red-400">Passwords do not match</p>
                  )}
                  {confirmPassword && password === confirmPassword && password?.length >= 8 && (
                    <p className="text-xs mt-1 text-green-400">Passwords match ✓</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)', color: '#0a1628' }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating Password…
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/admin-login', { replace: true })}
                  className="text-sm text-white/30 hover:text-white/60 transition-colors"
                >
                  ← Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
